import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Clock, ChevronDown, ChevronRight, Download } from "lucide-react";

/** Format a duration in seconds as "Xh Ymin". Rounded down to the minute. */
function fmtDuration(seconds) {
  if (!seconds || seconds < 60) return "<1 min";
  const total = Math.floor(seconds / 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
}

/** Format a Naples-local YYYY-MM-DD as "vie 15 ago". */
function fmtDayLabel(ymd) {
  const [y, mo, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, mo - 1, d, 12));
  return dt.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short", timeZone: "America/New_York" });
}

function todayISO() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}
function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}
// Accounting week = Friday → Thursday (Naples). Hours reset every Friday.
function daysSinceFriday() {
  const wd = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", weekday: "short" }).format(new Date());
  const i = ["Fri", "Sat", "Sun", "Mon", "Tue", "Wed", "Thu"].indexOf(wd);
  return i < 0 ? 0 : i;
}
const thisWeek = () => ({ start: isoDaysAgo(daysSinceFriday()), end: todayISO() });
const prevWeek = () => ({ start: isoDaysAgo(daysSinceFriday() + 7), end: isoDaysAgo(daysSinceFriday() + 1) });

export default function AuthLogDialog({ open, onOpenChange }) {
  // Solo dispatchers — el seguimiento de horas aplica únicamente a este rol.
  const role = "dispatcher";
  const [start, setStart] = useState(thisWeek().start);
  const [end, setEnd] = useState(thisWeek().end);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/auth-log", { params: { role, start, end } });
      setRows(data?.rows || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [role, start, end]);

  useEffect(() => {
    if (open) {
      // Every time the dialog opens, land on the CURRENT accounting week so
      // the dispatcher counters "reset" each Friday.
      const w = thisWeek();
      setStart(w.start);
      setEnd(w.end);
    }
  }, [open]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const applyWeek = (w) => { setStart(w.start); setEnd(w.end); };
  const isThisWeek = start === thisWeek().start && end === thisWeek().end;
  const isPrevWeek = start === prevWeek().start && end === prevWeek().end;

  const toggle = (uid) => setExpanded((s) => ({ ...s, [uid]: !s[uid] }));

  const exportCSV = () => {
    if (!rows.length) {
      toast.error("No hay datos que exportar");
      return;
    }
    // Two sections: (1) session-level rows for pivot-ready payroll math,
    // (2) per-user weekly totals for quick tally. Uses ISO YMD + Naples-local
    // time strings so payroll software imports without timezone surprises.
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [];
    // Section 1: sessions
    lines.push([
      "Usuario", "Email", "Rol", "Fecha (Naples)",
      "Login (Naples)", "Logout (Naples)",
      "Duración (h:mm)", "Duración (horas decimal)",
      "Estado",
    ].join(","));
    rows.forEach((r) => {
      (r.sessions || []).forEach((s) => {
        const day = (s.login_naples || "").slice(0, 10);
        const loTime = (s.login_naples || "").slice(11);
        const hiTime = (s.logout_naples || "").slice(11);
        const mins = Math.floor((s.duration_seconds || 0) / 60);
        const hMM = `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, "0")}`;
        const decimal = ((s.duration_seconds || 0) / 3600).toFixed(2);
        const estado = s.still_open ? "EN LÍNEA" : s.auto_closed ? "reemplazada" : "cerrada";
        lines.push([r.user_name, r.user_email, r.role, day, loTime, hiTime, hMM, decimal, estado].map(esc).join(","));
      });
    });
    // Blank line + Section 2: weekly totals per user
    lines.push("");
    lines.push(["Usuario", "Email", "Rol", "Semana desde (viernes)", "Total (h:mm)", "Total (horas decimal)"].join(","));
    rows.forEach((r) => {
      (r.per_week || []).forEach((w) => {
        const mins = Math.floor((w.seconds || 0) / 60);
        const hMM = `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, "0")}`;
        const decimal = ((w.seconds || 0) / 3600).toFixed(2);
        lines.push([r.user_name, r.user_email, r.role, w.week_start, hMM, decimal].map(esc).join(","));
      });
    });
    // Blank line + Section 3: per-user grand total for the window
    lines.push("");
    lines.push(["Usuario", "Email", "Rol", `Total del período (${start} → ${end})`, "Horas decimal"].join(","));
    rows.forEach((r) => {
      const mins = Math.floor((r.total_seconds || 0) / 60);
      const hMM = `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, "0")}`;
      const decimal = ((r.total_seconds || 0) / 3600).toFixed(2);
      lines.push([r.user_name, r.user_email, r.role, hMM, decimal].map(esc).join(","));
    });

    // Trigger a client-side download. BOM prefix so Excel opens it as UTF-8
    // without mangling tildes/ñ.
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `horas-conexion_${role}_${start}_${end}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="auth-log-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" /> Historial de conexiones — Naples (EDT)
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3 flex-shrink-0 pb-2 border-b border-border">
          <div className="flex items-center gap-1.5 w-full">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Semana contable (vie → jue)</span>
            <button
              type="button" onClick={() => applyWeek(thisWeek())} data-testid="auth-log-this-week"
              className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${isThisWeek ? "bg-emerald-500 text-black border-emerald-500" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25"}`}
            >
              Esta semana
            </button>
            <button
              type="button" onClick={() => applyWeek(prevWeek())} data-testid="auth-log-prev-week"
              className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${isPrevWeek ? "bg-sky-500 text-black border-sky-500" : "bg-sky-500/15 text-sky-300 border-sky-500/30 hover:bg-sky-500/25"}`}
            >
              Semana anterior
            </button>
            <span className="text-[11px] font-mono text-muted-foreground ml-auto" data-testid="auth-log-range-label">
              {fmtDayLabel(start)} → {fmtDayLabel(end)}
            </span>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Desde</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="auth-log-start" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Hasta</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="auth-log-end" />
          </div>
          <Button onClick={load} disabled={loading} className="bg-primary text-primary-foreground font-bold" data-testid="auth-log-refresh">
            {loading ? "Cargando..." : "Actualizar"}
          </Button>
          <Button
            onClick={exportCSV}
            disabled={!rows.length}
            variant="outline"
            className="border-primary/50 text-primary hover:bg-primary/10 font-bold"
            data-testid="auth-log-export-csv"
            title="Exportar sesiones y totales a CSV"
          >
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 py-3">
          {rows.length === 0 && !loading && (
            <div className="text-center text-sm text-muted-foreground py-8">
              Sin actividad en este rango.
            </div>
          )}

          {rows.map((r) => {
            const isOpen = expanded[r.user_id];
            const weekly = r.per_week?.map((w) => (
              <span key={w.week_start} className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[11px] font-bold border border-primary/50 text-primary bg-primary/10 mr-2 mb-1">
                Sem {fmtDayLabel(w.week_start).slice(-6)}: {fmtDuration(w.seconds)}
              </span>
            ));
            return (
              <Card key={r.user_id} className="bg-card border-border">
                <button
                  onClick={() => toggle(r.user_id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-primary/5 transition-colors text-left"
                  data-testid={`auth-log-user-${r.user_id}`}
                >
                  <div className="flex items-center gap-2">
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <div>
                      <div className="font-bold">{r.user_name}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{r.user_email}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
                    <div className="text-lg font-black font-mono text-primary">{fmtDuration(r.total_seconds)}</div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-border p-4 space-y-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Horas por semana (viernes → jueves)</div>
                      <div>{weekly?.length ? weekly : <span className="text-xs text-muted-foreground">Sin registro</span>}</div>
                    </div>

                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Horas por día</div>
                      <div className="flex flex-wrap gap-1.5">
                        {r.per_day?.length ? r.per_day.map((d) => (
                          <span key={d.ymd} className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[11px] border border-border">
                            {fmtDayLabel(d.ymd)}: <strong className="text-foreground">{fmtDuration(d.seconds)}</strong>
                          </span>
                        )) : <span className="text-xs text-muted-foreground">Sin registro</span>}
                      </div>
                    </div>

                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Sesiones (Naples EDT)</div>
                      <div className="max-h-56 overflow-y-auto border border-border rounded">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/30 sticky top-0">
                            <tr>
                              <th className="text-left px-2 py-1 font-mono font-normal">Login</th>
                              <th className="text-left px-2 py-1 font-mono font-normal">Logout</th>
                              <th className="text-right px-2 py-1 font-mono font-normal">Duración</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.sessions?.length ? r.sessions.map((s, i) => (
                              <tr key={i} className="border-t border-border/50">
                                <td className="px-2 py-1 font-mono">{s.login_naples}</td>
                                <td className="px-2 py-1 font-mono">
                                  {s.logout_naples}
                                  {s.still_open && <span className="ml-2 text-[10px] font-bold text-emerald-500">EN LÍNEA</span>}
                                  {s.auto_closed && <span className="ml-2 text-[10px] text-amber-500">reemplazada</span>}
                                  {s.closed_by_heartbeat && <span className="ml-2 text-[10px] text-orange-400" title="Sesión cerrada automáticamente al detectar inactividad (>90s sin heartbeat)">cerrada por inactividad</span>}
                                </td>
                                <td className="px-2 py-1 font-mono text-right">{fmtDuration(s.duration_seconds)}</td>
                              </tr>
                            )) : (
                              <tr><td colSpan={3} className="text-center text-muted-foreground py-2">Sin sesiones</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
